// Get API URL from environment variable.
// In production this is set before the frontend is built.
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (apiUrl) {
    return apiUrl;
  }

  if (process.env.NODE_ENV === "development") {
    return "http://localhost:3001/api";
  }

  return "/api";
};

const API_BASE_URL = getApiBaseUrl();

function authToken() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("auth_token");
}

function authHeaders(extra) {
  const headers = { ...(extra || {}) };
  const token = authToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function apiFetch(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: authHeaders(options.headers),
  });
  if (response.status === 401 && typeof window !== "undefined") {
    localStorage.removeItem("auth_token");
    window.location.reload();
  }
  return response;
}

export async function loginWithGoogle(credential) {
  const response = await fetch(`${API_BASE_URL}/auth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Sign-in failed");
  }
  return data;
}

export async function verifySession() {
  const token = authToken();
  if (!token) return false;
  const response = await fetch(`${API_BASE_URL}/auth/verify`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!response.ok) {
    localStorage.removeItem("auth_token");
    return false;
  }
  return true;
}

export async function logout() {
  const token = authToken();
  if (token) {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      headers: authHeaders(),
    }).catch(() => {});
  }
  localStorage.removeItem("auth_token");
}

export async function getPatterns() {
  const response = await apiFetch("/patterns");
  if (!response.ok) {
    throw new Error("Failed to fetch patterns");
  }
  return response.json();
}

export async function getPattern(id) {
  const response = await apiFetch(`/patterns/${id}`);
  if (!response.ok) {
    throw new Error("Failed to fetch pattern");
  }
  return response.json();
}

export async function createPattern(pattern) {
  const response = await apiFetch("/patterns", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(pattern),
  });

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("text/html")) {
    throw new Error(
      "API URL misconfigured - received HTML instead of JSON. Please ensure REACT_APP_API_URL is set correctly and the frontend is rebuilt."
    );
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to create pattern: ${response.status} ${errorText}`);
  }
  return response.json();
}

export async function updatePattern(id, updates) {
  const response = await apiFetch(`/patterns/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error("Failed to update pattern");
  }
  return response.json();
}

export async function deletePattern(id) {
  const response = await apiFetch(`/patterns/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw new Error("Failed to delete pattern");
  }
}

export async function getColorPalettes() {
  const response = await apiFetch("/color-palettes");
  if (!response.ok) {
    throw new Error("Failed to fetch color palettes");
  }
  return response.json();
}

export async function createColorPalette(payload) {
  const response = await apiFetch("/color-palettes", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("text/html")) {
    throw new Error("API URL misconfigured - received HTML instead of JSON.");
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to save color palette: ${response.status} ${errorText}`
    );
  }
  return response.json();
}

export async function updateColorPalette(id, updates) {
  const response = await apiFetch(`/color-palettes/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Failed to update color palette: ${response.status} ${errorText}`
    );
  }
  return response.json();
}

export async function deleteColorPalette(id) {
  const response = await apiFetch(`/color-palettes/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw new Error("Failed to delete color palette");
  }
}

/**
 * Proxied [Palettes Pro Color Harmony](https://palettespro.com/color-harmony).
 * @param {{ color: string, schemes?: string, formats?: string }} opts
 * @returns {Promise<Record<string, unknown>>}
 */
export async function fetchPalettesColorHarmony(opts) {
  const { color, schemes, formats } = opts;
  const params = new URLSearchParams();
  params.set("color", color);
  if (schemes) params.set("schemes", schemes);
  if (formats) params.set("formats", formats);
  const response = await apiFetch(`/palette/color-harmony?${params.toString()}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      data.error ||
        data.detail ||
        `Palettes harmony request failed (${response.status})`
    );
  }
  if (data && typeof data === "object" && data.error) {
    throw new Error(String(data.error));
  }
  return data;
}
